// ── Subcontractor agreement amendments ──────────────────────────────────────
// A new version of the subcontractor agreement is published as a list of
// amendments to the version before (contract_amendments, 20261286). Each
// amendment is one or more exact changes, {find, replace}, applied to the
// previous text. Drivers who signed the previous version read and agree the
// amendments one by one instead of re-reading the whole agreement.
//
// Pure functions only: used by the portal and by scripts/build-contract-amendments.mjs.

/**
 * How a change reads to a person: wording added, removed, or replaced.
 * An insertion is written as find → find + new text (or new text + find), so
 * the anchor is never shown as if it had changed.
 */
export function describeChange({ find = '', replace = '' } = {}) {
  if (replace === '') return { kind: 'removed', before: find.trim(), after: '' };
  if (replace.startsWith(find)) return { kind: 'added', before: '', after: replace.slice(find.length).trim() };
  if (replace.endsWith(find)) return { kind: 'added', before: '', after: replace.slice(0, replace.length - find.length).trim() };
  return { kind: 'changed', before: find.trim(), after: replace.trim() };
}

/** The new version's text: every change applied to the previous text, each find exactly once. */
export function applyAmendments(previousText, amendments) {
  let text = String(previousText);
  for (const a of amendments) {
    for (const c of a.changes) {
      const n = previousText.split(c.find).length - 1;
      if (n !== 1) throw new Error(`Amendment ${a.position}: wording to replace found ${n} times, expected once`);
      if (text.split(c.find).length - 1 !== 1) throw new Error(`Amendment ${a.position}: wording overlaps an earlier change`);
      text = text.replace(c.find, () => c.replace);
    }
  }
  return text;
}

/** Amendments not yet agreed, in order. */
export function amendmentsLeft(amendments, agreedIds) {
  const agreed = new Set(agreedIds);
  return [...amendments].sort((a, b) => a.position - b.position).filter((a) => !agreed.has(a.id));
}

/**
 * The record appended to a signed amendment contract: which amendments were
 * agreed, and when. Times are ISO strings from the acceptances.
 */
export function amendmentsRecord(amendments, acceptances) {
  const at = Object.fromEntries((acceptances || []).map((x) => [x.amendment_id, x.agreed_at]));
  const lines = [...amendments].sort((a, b) => a.position - b.position)
    .map((a) => `${a.position}. ${a.title} (clause ${a.clause_ref}): agreed ${at[a.id] || 'NOT AGREED'}`);
  return ['--- AMENDMENTS AGREED ---', ...lines, '-------------------------'].join('\n');
}
